/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetResponsesPerPageParam, HireStatusParams, ResponseItem, VacancyItem } from '../../api/interfaces';
import { VacancyItemAll } from '../../components/FilterSearch/DesktopSelects/VacancySelect';
import { DatePeriod, HireStatus, ResponseType } from '../../enums';

export interface ResponsesCounters {
    applied: number;
    invited: number;
    mutual: number;
    appliedNew: number;
    mutualNew: number;
}

export interface ResponsesFilters {
    vacancyId: number;
    hireStatus: HireStatus;
    datePeriod: DatePeriod;
    dateFrom: Date | undefined;
    dateTo: Date | undefined;
}

export interface ResponsesListPagination {
    totalCount: number;
    currentPage: number;
    pageCount: number;
    perPage: GetResponsesPerPageParam;
}

export interface ResponsesState {
    type: ResponseType;
    counters: ResponsesCounters;
    filters: ResponsesFilters;
    responses: ResponseItem[];
    pagination: ResponsesListPagination;
    vacancySelectOptions: VacancyItem[];
    workerSearch: string;
    responseHistory: ResponseItem[];
    highlightArchivationIcon: boolean;
}

const countersInitialState: ResponsesCounters = {
    applied: 0,
    invited: 0,
    mutual: 0,
    appliedNew: 0,
    mutualNew: 0,
};

const filtersInitialState: ResponsesFilters = {
    vacancyId: VacancyItemAll.id,
    hireStatus: HireStatus.All,
    datePeriod: DatePeriod.AllTime,
    dateFrom: undefined,
    dateTo: undefined,
};

const paginationInitialState: ResponsesListPagination = {
    totalCount: 0,
    currentPage: 1,
    pageCount: 1,
    perPage: 20,
};

const initialState: ResponsesState = {
    type: ResponseType.Applied,
    counters: countersInitialState,
    filters: filtersInitialState,
    responses: [],
    pagination: paginationInitialState,
    vacancySelectOptions: [],
    workerSearch: '',
    responseHistory: [],
    highlightArchivationIcon: false,
};

export const responsesSlice = createSlice({
    name: 'employer__responses',
    initialState,
    reducers: {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        fetchResponseTypeTabData: () => {},

        getResponsesCounters: () => {},
        setResponsesCounters: (state, action: PayloadAction<ResponsesCounters>) => {
            state.counters = action.payload;
        },

        getResponses: () => {},
        setResponses: (
            state,
            action: PayloadAction<{ data: ResponseItem[]; pageCount?: number; totalCount?: number }>,
        ) => {
            state.responses = action.payload.data;
            if (action.payload.pageCount) {
                state.pagination.pageCount = action.payload.pageCount;
            }
            if (action.payload.totalCount) {
                state.pagination.totalCount = action.payload.totalCount;
            }
        },

        getVacancyList: () => {},
        setVacancyList: (state, action: PayloadAction<VacancyItem[]>) => {
            state.vacancySelectOptions = action.payload;
        },

        setResponseType: (state, action: PayloadAction<ResponseType>) => {
            state.type = action.payload;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        setFiltersVacancy: (state, action: PayloadAction<number>) => {
            state.filters.vacancyId = action.payload;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        setFiltersHireStatus: (state, action: PayloadAction<HireStatus>) => {
            state.filters.hireStatus = action.payload;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        setFiltersDatePeriod: (state, action: PayloadAction<DatePeriod>) => {
            state.filters.datePeriod = action.payload;
        },

        setFiltersDates: (state, action: PayloadAction<{ dateFrom: Date | undefined; dateTo: Date | undefined }>) => {
            state.filters.dateFrom = action.payload.dateFrom;
            state.filters.dateTo = action.payload.dateTo;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        setWorkerSearch: (state, action: PayloadAction<string>) => {
            state.workerSearch = action.payload;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        clearFilters: (state) => {
            state.filters = filtersInitialState;
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
        },

        setResponseStatusRequest: {
            reducer: () => {},
            prepare: (
                responseId: number,
                hireStatus: HireStatus.Invited | HireStatus.InProgress | HireStatus.Hired,
            ) => ({
                payload: { responseId, hireStatus },
            }),
        },
        setResponseStatusSuccess: (
            state,
            action: PayloadAction<{
                responseId: number;
                hireStatus: HireStatusParams;
            }>,
        ) => {
            state.responses = state.responses.map((r) => {
                if (r.id === action.payload.responseId) {
                    r.hireStatus = action.payload.hireStatus;
                }

                return r;
            });
            if (state.responseHistory.length) {
                state.responseHistory = state.responseHistory.map((i) => {
                    if (i.id === action.payload.responseId) {
                        i.hireStatus = action.payload.hireStatus;
                    }
                    return i;
                });
            }
        },

        setResponseArchivationStatusRequest: {
            reducer: () => {},
            prepare: (responseId: number, isArchived: boolean) => ({
                payload: { responseId, isArchived },
            }),
        },
        setResponseArchivationStatusSuccess: (state, action: PayloadAction<number>) => {
            state.responses = state.responses.map((r) => {
                if (r.id === action.payload) {
                    r.isArchived = true;
                }

                return r;
            });
        },
        removeResponseFromList: (state, action: PayloadAction<number>) => {
            state.responses = state.responses.filter((r) => r.id !== action.payload);
        },
        setHighlightArchivationIcon: (state, action: PayloadAction<boolean>) => {
            state.highlightArchivationIcon = action.payload;
        },

        getResponseHistoryByWorkerId: {
            reducer: () => {},
            prepare: (workerId: number) => ({
                payload: workerId,
            }),
        },
        setResponseHistory: (state, action: PayloadAction<ResponseItem[]>) => {
            state.responseHistory = action.payload;
        },

        markNewResponseAsReadRequest: {
            reducer: () => {},
            prepare: (responseId: number) => ({
                payload: responseId,
            }),
        },
        markNewResponseAsReadSuccess: (state, action: PayloadAction<number>) => {
            state.responses = state.responses.map((r) => {
                if (r.id === action.payload) {
                    r.isNew = false;
                }
                return r;
            });
        },

        setPaginationPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage = action.payload;
        },
        setPaginationPerPage: (state, action: PayloadAction<GetResponsesPerPageParam>) => {
            state.pagination.totalCount = 0;
            state.pagination.currentPage = 1;
            state.pagination.pageCount = 1;
            state.pagination.perPage = action.payload;
        },

        translateWorkerDescription: {
            reducer: () => {},
            prepare: (responseId: number) => ({
                payload: responseId,
            }),
        },
        setTranslationWorkerDescription: (
            state,
            action: PayloadAction<{ responseId: number; descriptionTranslated: string }>,
        ) => {
            state.responses = state.responses.map((r) => {
                if (r.id === action.payload.responseId) {
                    r.worker.descriptionTranslated = action.payload.descriptionTranslated;
                }
                return r;
            });
        },
    },
});

export const responsesActions = responsesSlice.actions;
export const responsesReducer = responsesSlice.reducer;
